/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'

import {
  EuiButton,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'
import type { ProjectType, ProjectID } from '@modules/ui-types/projects'

import ApiRequestModal from './ApiRequestModal'
import SelectionSummaryAccordion from './SelectionSummaryAccordion'

type Props = {
  projectId: ProjectID
  projectType: ProjectType
  selectedProjects: AnyProject[]
  projectName: string
  isEditing: boolean
}

const ReviewAndSaveStep: React.FunctionComponent<Props> = ({
  projectId,
  projectType,
  selectedProjects,
  projectName,
  isEditing,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false)

  const closeModal = () => setIsModalVisible(false)
  const showModal = () => setIsModalVisible(true)

  return (
    <EuiFlexGroup direction='column' gutterSize='m' style={{ maxWidth: '80rem' }}>
      <EuiFlexItem>
        <EuiPanel>
          <EuiFlexGroup direction='row' alignItems='center' justifyContent='spaceBetween'>
            <EuiFlexItem>
              <EuiText>
                <p>
                  <FormattedMessage
                    id='cps.review.summary'
                    defaultMessage='You have selected <strong>{count, plural, one {# {additional}project} other {# {additional}projects}}</strong> to link from your {projectName} project.'
                    values={{
                      count: selectedProjects.length,
                      projectName: <strong>{projectName}</strong>,
                      strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                      additional: isEditing ? 'additional ' : '',
                    }}
                  />
                </p>
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton
                onClick={showModal}
                color='neutral'
                data-test-subj='cps-reviewAndSaveStep-viewApiRequestButton'
              >
                <FormattedMessage
                  id='cps.review.viewApiRequest'
                  defaultMessage='View API request'
                />
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>

          <EuiHorizontalRule margin='m' />

          <EuiCallOut color='warning'>
            <EuiText>
              {isEditing ? (
                <Fragment>
                  <FormattedMessage
                    id='cps.review.linkBehavior.editing'
                    defaultMessage='After these projects are linked, searches from this project will also include results from these newly linked projects wherever they match the existing cross-project search (CPS) scope'
                  />
                  <br />
                  <FormattedMessage
                    id='cps.review.linkBehavior.editing.description'
                    defaultMessage='Consider updating your requests to adjust their scope.'
                  />
                </Fragment>
              ) : (
                <Fragment>
                  <FormattedMessage
                    id='cps.review.linkBehavior'
                    defaultMessage='After these projects are linked, all searches from this project will query every linked project by default.'
                  />
                  <br />
                  <FormattedMessage
                    id='cps.review.linkBehaviorImmediate'
                    defaultMessage='This applies immediately to all queries, including those from existing dashboards and alerting rules.'
                  />
                </Fragment>
              )}
            </EuiText>
          </EuiCallOut>

          <EuiSpacer />

          <EuiText>
            <p>
              <FormattedMessage
                id='cps.review.customizeBehavior'
                defaultMessage='In the project, you can define a default cross-project search scope for each space. Users can use that default, or select the scope of their choice when searching and filtering data or when running queries.'
              />
            </p>
          </EuiText>

          <EuiHorizontalRule margin='m' />

          <SelectionSummaryAccordion selectedProjects={selectedProjects} />

          <EuiSpacer size='m' />
        </EuiPanel>
      </EuiFlexItem>

      {isModalVisible && (
        <ApiRequestModal
          projectId={projectId}
          projectType={projectType}
          selectedProjects={selectedProjects}
          onClose={closeModal}
        />
      )}
    </EuiFlexGroup>
  )
}

export default ReviewAndSaveStep
